import { useEffect, useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { setStoredToken } from '../auth.js'

export default function Register() {
  const { t } = useTranslation()
  
  const [form, setForm] = useState({ email: '', password: '', display_name: '', gender: 'unspecified' })
  const [roleLabel, setRoleLabel] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [visible, setVisible] = useState(false)
  const [requiresVerification, setRequiresVerification] = useState(false)
  const [verificationEmail, setVerificationEmail] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80)
    return () => clearTimeout(t)
  }, [])

  function update(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }))
  }

  const inviteCode = searchParams.get('invite')

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await api.auth.register(form.email, form.password, form.display_name, inviteCode, { role_label: roleLabel || undefined, gender: form.gender })
      if (data.requires_verification) {
        setRequiresVerification(true)
        setVerificationEmail(data.email)
      } else {
        setStoredToken(data.token)
        const joinCode = searchParams.get('join')
        const familyInvite = searchParams.get('familyInvite')
        navigate(familyInvite ? `/family-invite/${familyInvite}` : joinCode ? `/join/${joinCode}` : '/dashboard')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleResendVerification() {
    setResendLoading(true)
    setError('')
    try {
      await api.auth.resendVerification(verificationEmail)
      setError('') // Clear any previous errors
      // Show success message by temporarily updating error state
      const msg = t('register.resendSuccess')
      setError(msg)
      setTimeout(() => setError(''), 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col justify-center bg-gradient-to-b from-indigo-600 to-indigo-800 px-4 dark:from-[#08101c] dark:via-[#0d1626] dark:to-slate-900 md:py-8">

      <div className="max-w-sm mx-auto w-full">
        <div className="text-center mb-8">
          <img src="/icons/logo.png" alt="Grannäventyret" className="mx-auto mb-3 w-full max-w-xs animate-float-pulse drop-shadow-lg" />
          <p className="mt-1 text-white/70 dark:text-slate-400">{t('register.tagline')}</p>
        </div>

        <div className={`rounded-2xl bg-white p-6 shadow-xl transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} dark:bg-slate-900 dark:shadow-black/20`}>
           {requiresVerification ? (
            <>
              <div className="text-center mb-6">
                <p className="text-4xl mb-4">📧</p>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-2">{t('register.verifyTitle')}</h2>
                <p className="text-sm text-gray-600 dark:text-slate-400">{t('register.verifySentTo', { email: verificationEmail })}</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {t('register.verifyInstructions')}
                </p>
              </div>
              {error && (
                <p className={`rounded-lg px-3 py-2 text-sm mb-4 ${
                  error.includes('skickat') 
                    ? 'bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-300'
                    : 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300'
                }`}>
                  {error}
                </p>
              )}
               <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="w-full rounded-lg bg-sky-600 py-3 font-semibold text-white transition btn-pop shadow-md shadow-sky-500/30 hover:bg-sky-700 disabled:opacity-50 dark:bg-sky-500 dark:text-slate-950 dark:hover:bg-sky-400 mb-3"
              >
                {resendLoading ? t('register.sending') : t('register.resend')}
              </button>
              <p className="text-center text-sm text-gray-500 dark:text-slate-400">
                <button
                  type="button"
                  onClick={() => setRequiresVerification(false)}
                  className="font-medium text-sky-600 hover:underline dark:text-sky-300"
                >
                  {t('register.backToRegister')}
                </button>
              </p>
            </>
           ) : (
            <>
              {inviteCode && (
                <div className="mb-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                  <svg className="h-4 w-4 flex-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  {t('register.invited')}
                </div>
              )}
              <h2 className="mb-5 text-xl font-semibold text-gray-800 dark:text-slate-100">{t('register.title')}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-slate-300">{t('register.name')}</label>
              <input
                type="text"
                value={form.display_name}
                onChange={update('display_name')}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-200 focus:ring-2 focus:ring-sky-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                placeholder="Anna Andersson"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-slate-300">{t('register.email')}</label>
              <input
                type="email"
                value={form.email}
                onChange={update('email')}
                required
                autoComplete="email"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-200 focus:ring-2 focus:ring-sky-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                placeholder="din@epost.se"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">{t('register.role')}</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'parent', emoji: '👨‍👩‍👧' },
                  { key: 'grandparent', emoji: '👴' },
                  { key: 'relative', emoji: '👨‍👦' },
                  { key: 'other', emoji: '🧑' },
                ].map(r => (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setRoleLabel(t(`register.roles.${r.key}`))}
                    className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                      roleLabel === t(`register.roles.${r.key}`)
                        ? 'border-sky-500 bg-sky-500/20 text-sky-300'
                        : 'border-white/20 text-white/70 hover:border-white/40'
                    }`}
                  >
                    {r.emoji} {t(`register.roles.${r.key}`)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">{t('register.gender')}</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'female', key: 'female' },
                  { value: 'male', key: 'male' },
                  { value: 'unspecified', key: 'unspecified' },
                ].map(g => (
                  <button
                    key={g.value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, gender: g.value }))}
                    className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                      form.gender === g.value
                        ? 'border-sky-500 bg-sky-500/20 text-sky-300'
                        : 'border-white/20 text-white/70 hover:border-white/40'
                    }`}
                  >
                    {t(`register.genders.${g.key}`)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-slate-300">{t('register.password')}</label>
              <input
                type="password"
                value={form.password}
                onChange={update('password')}
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-200 focus:ring-2 focus:ring-sky-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                placeholder={t('register.passwordPlaceholder')}
              />
            </div>
            {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-300">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-sky-600 py-3 font-semibold text-white transition btn-pop shadow-md shadow-sky-500/30 hover:bg-sky-700 disabled:opacity-50 dark:bg-sky-500 dark:text-slate-950 dark:hover:bg-sky-400"
            >
              {loading ? t('register.submitting') : t('register.submit')}
            </button>
          </form>
               <p className="mt-4 text-center text-sm text-gray-500 dark:text-slate-400">
                 {t('register.hasAccount')}{' '}
                 <Link to="/login" className="font-medium text-sky-600 hover:underline dark:text-sky-300">
                   {t('register.loginLink')}
                 </Link>
               </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
